import * as Tone from 'tone';
import EFFECT_MANIFEST from './manifest.js';
import { getToneProperty, setToneValue, setToneValueImmediate } from '../../toneParams.js';




function applyFixedParams(node, fixed) {
  if (!fixed) return;
  Object.entries(fixed).forEach(([property, value]) => {
    if (typeof value === 'undefined') return;
    setToneValueImmediate(node, property, value);
  });
}

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function createModule({ node, manifest, moduleSpec }) {
  const range = moduleSpec.valueRange || manifest.userParamSpec?.range || { min: 0, max: 100 };
  const parameterMappings = moduleSpec.parameterMappings || {};
  const mappedParameters = Object.keys(parameterMappings);
  const primaryParam = moduleSpec.control?.audioParam || mappedParameters[0] || moduleSpec.target;
  let automationBridge = null;

  const getTargetParam = () => {
    const first = mappedParameters[0] || primaryParam;
    return first ? getToneProperty(node, first) : null;
  };

  const fixedParams = Object.entries(moduleSpec.fixed || {}).map(([property, value]) => ({
    property,
    value,
  }));

  const applyValue = (value) => {
    const numeric = toNumber(value);
    if (numeric == null) return;

    try {
      const normalized = (numeric - range.min) / (range.max - range.min);
      
      // Extract segments
      const isNegativeSegment = normalized < 0.5;
      const segment = isNegativeSegment ? moduleSpec.segments.negative : moduleSpec.segments.positive;
      const localNorm = isNegativeSegment ? normalized * 2 : (normalized - 0.5) * 2;

      if (!segment?.parameterMappings) {
        console.warn('[ToneAutoWahEffect] No segment mappings found');
        return;
      }

      // All params: continuous updates (no throttling)
      const sensitivityRange = segment.parameterMappings.sensitivity;
      const baseFreqRange = segment.parameterMappings.baseFrequency;
      const octavesRange = segment.parameterMappings.octaves;
      const wetRange = segment.parameterMappings.wet;

      const sensitivityValue = sensitivityRange.min + localNorm * (sensitivityRange.max - sensitivityRange.min);
      const baseFreqValue = baseFreqRange.min + localNorm * (baseFreqRange.max - baseFreqRange.min);
      const octavesValue = octavesRange.min + localNorm * (octavesRange.max - octavesRange.min);
      const wetValue = wetRange.min + localNorm * (wetRange.max - wetRange.min);

      // Apply all parameters continuously
      if (automationBridge?.ramp) {
        automationBridge.ramp(sensitivityValue);
      } else {
        setToneValue(node, 'sensitivity', sensitivityValue);
      }
      
      setToneValue(node, 'baseFrequency', baseFreqValue);
      setToneValue(node, 'octaves', octavesValue);
      setToneValue(node, 'wet', wetValue);
    } catch (error) {
      console.warn('[ToneAutoWahEffect] Failed to apply value', error);
    }
  };

  const userParam = {
    id: manifest.userParamSpec?.id ?? manifest.inputParam,
    label: manifest.userParamSpec?.label ?? 'Input',
    toneProperty: mappedParameters.join(',') || primaryParam || '',
    tonePropertyTargets: mappedParameters.length ? mappedParameters : (primaryParam ? [primaryParam] : []),
    description: moduleSpec.description,
    valueRange: range,
    applyValue,
    getTargetParam,
  };

  return {
    id: moduleSpec.id,
    label: moduleSpec.label,
    description: moduleSpec.description,
    inputParam: manifest.inputParam,
    toneProperty: userParam.toneProperty,
    tonePropertyDescription: moduleSpec.description,
    valueRange: range,
    fixedParams,
    getTargetParam,
    applyValue,
    configure() {
      applyFixedParams(node, moduleSpec.fixed);
    },
    setAutomationBridge(bridge) {
      automationBridge = bridge;
    },
  };
}

export function createToneAutoWahEffect({ Tone, settings } = {}) {
  if (!Tone?.AutoWah) {
    throw new Error('[ToneAutoWahEffect] Tone.AutoWah constructor is required.');
  }

  const isSettingsArray = Array.isArray(settings);
  const ctorArgs = isSettingsArray
    ? settings
    : [{ ...EFFECT_MANIFEST.defaults, ...(settings || {}) }];

  const node = isSettingsArray
    ? new Tone.AutoWah(...ctorArgs)
    : new Tone.AutoWah(ctorArgs[0]);

  const modules = EFFECT_MANIFEST.modules.map((moduleSpec) =>
    createModule({ node, manifest: EFFECT_MANIFEST, moduleSpec }),
  );

  modules[0]?.configure?.();

  return {
    id: EFFECT_MANIFEST.id,
    label: EFFECT_MANIFEST.label,
    version: EFFECT_MANIFEST.version,
    inputParam: EFFECT_MANIFEST.inputParam,
    node,
    modules,
    configureModule(moduleId) {
      const module = modules.find((item) => item.id === moduleId);
      module?.configure?.();
    },
    dispose() {
      modules.splice(0, modules.length);
      if (typeof node.dispose === 'function') {
        node.dispose();
      }
    },
    manifest: EFFECT_MANIFEST,
  };
}

export default createToneAutoWahEffect;
